import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'

import { isEmpty, isString } from 'lodash'

import { Breadcrumbs } from 'components/breadcrumbs'
import { getCategoryURL } from 'components/molecules/autocomplete/utils'
import HitResultTitle from 'components/molecules/hit-result-title'
import { useGetNavMenu } from 'features/layout/header/navbar/use-get-nav-menu'

import { Crumbs } from 'context/breadcrumbs'
import { useTranslate } from 'hooks'
import { useProductCategories } from 'hooks/use-product-categories'

import { handleCategoriesSplit } from 'utils/search/search-url-mapping'

import { ROUTES } from 'constants/routes'

import css from './search-header.module.scss'

type SearchHeaderProps = {
  setPageTitle: (title: string) => void
  isPromotionRoute: boolean
}

const SearchHeader = ({ setPageTitle, isPromotionRoute }: SearchHeaderProps) => {
  const { t: tran, lang } = useTranslate('search')
  const { t } = useTranslate('strapi-search-[...term]')
  const router = useRouter()
  const [breadcrumbs, setBreadcrumbs] = useState<Crumbs[]>([])
  const [searchTitle, setSearchTitle] = useState('')
  const { data: navMenuItemList } = useGetNavMenu()
  const { data: categoriesResult } = useProductCategories(lang)

  const categoryMapByNameEn = useMemo(() => {
    return categoriesResult?.categoryMapByNameEn ?? {}
  }, [categoriesResult])

  const isNewRoute = router.asPath.includes(ROUTES.SEARCH_PRODUCTS_NEW)

  const searchText = router.query.query as string

  useEffect(() => {
    if (!isEmpty(searchText)) {
      setBreadcrumbs([])
      setPageTitle(`Search - ${searchText}`)
      setSearchTitle('')
      return
    }

    const newCategoriesEN: string[] = isString(router.query.cat) ? handleCategoriesSplit(router.query.cat) : []

    const newCategoriesLang = newCategoriesEN.map((cat) =>
      isEmpty(categoryMapByNameEn[cat]) ? cat : categoryMapByNameEn[cat].name
    )

    const newBreadCrumbs: Crumbs[] = [
      {
        text: tran('common:home'),
        href: '/',
      },
    ]

    if (!isEmpty(newCategoriesEN) && !isPromotionRoute) {
      newCategoriesLang.forEach((catName, index) => {
        newBreadCrumbs.push({
          text: catName,
          href: getCategoryURL(
            newCategoriesEN.slice(0, index + 1),
            false,
            isNewRoute ? ROUTES.SEARCH_PRODUCTS_NEW : ROUTES.SEARCH_PRODUCTS
          ),
        })
      })
    }

    if (isNewRoute) {
      newBreadCrumbs.push({
        text: tran('New'),
        href: ROUTES.SEARCH_PRODUCTS_NEW,
      })
      setPageTitle(`${t('PageSearch.PageSearch_Page_search')} - ${tran('New')}`)
      setSearchTitle(tran('New'))
    } else if (isPromotionRoute) {
      const params = router.query.terms as string[]
      const promotionKey = params[1]
      const promotion = navMenuItemList?.find((nav) => nav.key === promotionKey && nav.tag === 'promotion')

      newBreadCrumbs.push({
        text: `${t('PageSearch.PageSearch_Page_promotion')} - ${promotion?.name ?? ''}`,
        href: '#',
      })

      setPageTitle(`${t('PageSearch.PageSearch_Page_search')} - ${t('PageSearch.PageSearch_Page_promotion')}`)
      setSearchTitle(t('PageSearch.PageSearch_Page_promotion'))
    } else {
      setPageTitle(
        `${t('PageSearch.PageSearch_Page_search')} - ${
          newCategoriesLang.length > 0 ? newCategoriesLang[newCategoriesLang.length - 1] : 'Markato'
        }`
      )
      setSearchTitle(
        newCategoriesLang.length > 0
          ? newCategoriesLang[newCategoriesLang.length - 1]
          : t('PageSearch.PageSearch_Page_all')
      )
    }

    setBreadcrumbs(newBreadCrumbs)
  }, [
    router.query.cat,
    router.query.terms,
    isNewRoute,
    isPromotionRoute,
    categoryMapByNameEn,
    searchText,
    tran,
    setPageTitle,
    t,
    navMenuItemList,
  ])

  return (
    <section className={css.container}>
      {breadcrumbs.length > 1 && <Breadcrumbs items={breadcrumbs} />}
      {searchTitle ? (
        <div className={css.searchHeader}>
          <h2 className={css.searchTitle}>{searchTitle}</h2>
        </div>
      ) : (
        !!searchText && <HitResultTitle searchText={searchText} />
      )}
    </section>
  )
}

export default SearchHeader
